// @flow

/**
 * @deprecated - replaced by the monetary amount module
 * the helper methods here do mind-bendingly stupid work
 * to do math on PriceGroupToMonetaryAmounMaps and arrays
 * of them.
 *
 * Going forward PriceGroupToMonetaryAmounMaps
 * will be quarantined to the Product module and converted to
 * monetary amounts by helpers there.
 *
 * The new strategy will be to operate on MonetaryAmounts
 * or arrays of MonetaryAmounts. Most functions here - that
 * don't mitigate the downsides of doing math using
 * PriceGroupToMonetaryAmounMaps - will be ported to MonetaryAmount
 * versions in the monetary amount module.
 */

import currency from '@elastic-internal/currencyformatter.js';
import identity from 'lodash/fp/identity';
import flatten from 'lodash/fp/flatten';
import mapValues from 'lodash/fp/mapValues';
import zip from 'lodash/fp/zip';

import { convertLocale } from './date-locale';

import type { PriceGroupToMonetaryAmountMap } from 'modules/monetary-amount';

/**
 * These fall into a couple different categories. The ones you'll most likely
 * want to actually use are {@link modules/price.sumPrices} and
 * {@link modules/price.multPrice} which are for performing basic arithmetic on
 * {@link Price} objects, {@link modules/price.renderPrice} and
 * {@link modules/price.renderPriceRange} which render
 * {@link Price} objects as HTML-safe strings, and
 * {@link modules/price.rangeForPriceList} which gets the high and low for a
 * list of {@link Price} objects.
 *
 * @module modules/price
 */

/**
 * The standard structure for transporting information about price. Just a
 * plain `Object` where the keys are price groups and the values are floats. In
 * theory there should always be price groups which match the Customer's
 * `wholesale_price_group` and `retail_price_group`.
 *
 * @typedef {Object} Price
 * @property {Float} * price group: price
 */

export type PriceRange = [
  PriceGroupToMonetaryAmountMap,
  PriceGroupToMonetaryAmountMap,
];
type ResolvedPriceForType = number | null;
type ResolvedPriceRangeForType = [ResolvedPriceForType, ResolvedPriceForType];
export type WithPricePriceType = 'wholesale' | 'retail';
export type PriceType = WithPricePriceType | 'both' | 'external' | 'none';

/**
 * This is a post-grouping price, prices get collapsed down to a single
 * wholesale and a single retail price based on the group tags  and settings
 * of the relevant user.
 *
 * @typedef {Object} ResolvedPrice
 * @memberof modules/price
 * @property {Float|null} wholesale
 * @property {Float|null} retail
 */
type ResolvedPrice = {
  wholesale: ResolvedPriceForType,
  retail: ResolvedPriceForType,
};

type ResolvedPriceRangeByTypeParams = $ReadOnly<{|
  range: PriceRange,
  priceType?: WithPricePriceType,
  wholesalePriceGroup: string,
  retailPriceGroup: string,
|}>;

type StrikeThroughRetailParams = $ReadOnly<{|
  range: PriceRange,
  priceType?: PriceType,
  wholesalePriceGroup: string,
  retailPriceGroup: string,
  wholesaleRepresentsDiscountedAmount?: boolean,
|}>;

type RenderPriceRangeByTypeParams = $ReadOnly<{|
  range: PriceRange,
  priceType?: WithPricePriceType,
  wholesalePriceGroup: string,
  retailPriceGroup: string,
  currencyCode?: string,
  locale?: string,
|}>;

type PriceParamsWithType = RenderPriceRangeByTypeParams;

/**
 * The callback function that handles merging left and right prices together.
 *
 * Examples:
 *
 * `(l, r) => l + r`
 *
 * `(l, r) => l * r`
 *
 * `(l, r) => Math.min(l, r)`
 *
 * @callback mergePrices_actor
 * @protected
 * @memberof modules/price
 * @param {Float} left
 * @param {Float} right
 * @returns {Float}
 */
type MergePricesActor = (number, number) => number;

export const DEFAULT_PRICE_GROUP: string = 'wholesale';

export type PriceParams = {|
  priceType: PriceType,
  wholesalePriceGroup: string,
  retailPriceGroup: string,
  currencyCode: string,
  locale: string,
  // actually required, update coming soon
  wholesaleRepresentsDiscountedAmount?: boolean,
|};

/**
 * Merge two prices together via `actor` callback.
 *
 * Basically the prices' keys are zipped together and `actor` defines the
 * strategy for merging the values.
 *
 * @memberof modules/price
 * @protected
 * @param {Object} params
 * @param {Price} params.left the left-hand operand
 * @param {Price} [params.right] the right-hand operand
 * @param {modules/price.mergePrices_actor} params.actor
 * @returns {Price}
 */
export const mergePrices = ({
  left,
  right = { ...null },
  actor,
}: {
  left: PriceGroupToMonetaryAmountMap,
  right?: PriceGroupToMonetaryAmountMap,
  actor: MergePricesActor,
}): PriceGroupToMonetaryAmountMap => // union all the price keys
  Object.keys(left)
    .concat(Object.keys(right))
    .reduce(
      (acc, key) => (acc.includes(key) ? acc : acc.concat([key])),
      [],
      // build a result with all the keys
    )
    .reduce(
      (acc, key) => ({
        ...acc,
        // anything that's undefined is 0
        // https://medium.com/flow-type/spreads-common-errors-fixes-9701012e9d58
        // $FlowFixMe[invalid-computed-prop]
        [key]: actor(left[key] || 0.0, right[key] || 0.0),
      }),
      { ...null },
    );

/**
 * Sum two prices together, an `undefined` on either side is treated as `0`.
 *
 * @memberof modules/price
 * @param {Object} params
 * @param {Price} params.left the left-hand operand
 * @param {Price} [params.right] the right-hand operand
 * @returns {Price}
 */
export const addPrices = (params: {
  left: PriceGroupToMonetaryAmountMap,
  right: PriceGroupToMonetaryAmountMap,
}): PriceGroupToMonetaryAmountMap =>
  mergePrices({ ...params, actor: (left, right) => left + right });

export const sumPrices = (
  prices: $ReadOnlyArray<PriceGroupToMonetaryAmountMap>,
): PriceGroupToMonetaryAmountMap =>
  prices.reduce((acc, price) => addPrices({ left: acc, right: price }), {
    ...null,
  });

export const addAmount = (
  amount: number,
  price: PriceGroupToMonetaryAmountMap,
): PriceGroupToMonetaryAmountMap => mapValues((val) => val + amount, price);

/**
 * Mulitply two prices together, an `undefined` on either side is streated as
 * `0`.
 *
 * @memberof modules/price
 * @param {Object} params
 * @param {Price} params.left the left-hand operand
 * @param {Price} [params.right] the right-hand operand
 * @returns {Price}
 */
export const multPrice = ({
  price,
  factor,
}: {
  price: PriceGroupToMonetaryAmountMap,
  factor: number,
}): PriceGroupToMonetaryAmountMap =>
  mergePrices({ left: price, actor: (amount) => amount * factor });

/**
 * Formats a Float as a currency String, handles currency code and localization
 *
 * @memberof modules/price
 * @see https://en.wikipedia.org/wiki/ISO_4217
 * @see http://cldr.unicode.org/
 * @param {Object} params
 * @param {Float} params.price
 * @param {String} [params.currencyCode='USD'] ISO 4217 currency code
 * @param {String} [params.locale]
 * @returns {String}
 */

// it has been requested to differentiate the currency symbol for the nordic countries
// which otherwise would be the same ('kr') for Denmark, Norway and Sweden
const currencyCodesWithAmbiguousSymbols: string[] = ['DKK', 'NOK', 'SEK'];

const currencyCodeIsAmbiguous = (currencyCode: string): boolean =>
  currencyCodesWithAmbiguousSymbols.includes(currencyCode);

export const priceToString = ({
  price,
  currencyCode = 'USD',
  locale,
  usesExternalPrices = false,
}: {
  price: ResolvedPriceForType,
  currencyCode?: string,
  locale?: string,
  usesExternalPrices?: boolean,
}): string => {
  // there must be some encoding issue with currencyformatter.js, i keep
  // getting irregular whitespace out of it
  const currencyLocale = locale ? convertLocale(locale, 'snake') : '';
  const priceString = currency
    .format(price, {
      currency: currencyCode,
      locale: currencyLocale.match(/^zh/i) ? 'zh' : currencyLocale,
    })
    .replace(/\s/g, ' ');

  // for currency codes that have an ambiguous corresponding currency symbol we return a
  // price string including both currency code and symbol
  if (currencyCodeIsAmbiguous(currencyCode))
    return usesExternalPrices
      ? `<span class="externalPrice">${currencyCode} ${priceString}</span>`
      : `${currencyCode} ${priceString}`;
  return usesExternalPrices
    ? `<span class="externalPrice">${priceString}</span>`
    : priceString;
};

export const pricesToString = ({
  prices,
  currencyCode = 'USD',
  locale,
  usesExternalPrices = false,
}: {
  prices: [ResolvedPriceForType] | ResolvedPriceRangeForType,
  currencyCode?: string,
  locale?: string,
  usesExternalPrices?: boolean,
}): string =>
  prices
    .map((price) =>
      priceToString({
        currencyCode,
        locale,
        price,
        usesExternalPrices,
      }),
    )
    .join('-');

/**
 * Handles price groups, collapses a {@link Price} into a wholesale
 * / retail pair based on the give `priceType` and price groups.
 *
 * If the `priceType` is `'wholesale'` then the retail price will always be `null`
 * and vice versa, and if the `priceType` is `'none'` then both will always be
 * `null`.
 *
 * @memberof modules/price
 * @protected
 * @param {Object} params
 * @param {Price} params.price
 * @param {String} [params.priceType='wholesale']
 * @param {String} [params.wholesalePriceGroup='wholesale']
 * @param {String} [params.retailPriceGroup='retail']
 * @returns {modules/price.ResolvedPrice}
 */
export const resolvedPrices = ({
  price,
  priceType = 'wholesale',
  wholesalePriceGroup,
  retailPriceGroup,
}: {
  price: PriceGroupToMonetaryAmountMap,
  priceType?: PriceType,
  wholesalePriceGroup: string,
  retailPriceGroup: string,
}): ResolvedPrice => ({
  wholesale: ['both', 'wholesale'].includes(priceType)
    ? price[wholesalePriceGroup] || 0
    : null,
  retail: ['both', 'retail'].includes(priceType)
    ? price[retailPriceGroup] || 0
    : null,
});

export const resolvedPriceRangeByType = ({
  range: [low, high],
  priceType = 'wholesale',
  ...restParams
}: ResolvedPriceRangeByTypeParams): ResolvedPriceRangeForType => [
  resolvedPrices({ ...restParams, priceType, price: low })[priceType],
  resolvedPrices({ ...restParams, priceType, price: high })[priceType],
];

export const retailEqualsWholesale = (params: {|
  +range: PriceRange,
  +wholesalePriceGroup: string,
  +retailPriceGroup: string,
|}): boolean =>
  zip(
    resolvedPriceRangeByType({ ...params, priceType: 'retail' }),
    resolvedPriceRangeByType({ ...params, priceType: 'wholesale' }),
  ).every(([r, w]) => r === w);

export const strikeThroughRetail = ({
  wholesaleRepresentsDiscountedAmount,
  priceType,
  ...retailEqualsWholesaleProps
}: StrikeThroughRetailParams): boolean =>
  Boolean(
    wholesaleRepresentsDiscountedAmount &&
      priceType === 'both' &&
      !retailEqualsWholesale(retailEqualsWholesaleProps),
  );

/**
 * Renders a price range as a string, separated with a hyphen if necessary,
 * collapsed to a single value if not. Only handles a single `priceType` at a
 * time.
 *
 * @memberof modules/price
 * @protected
 * @param {Object} params
 * @param {Price[]} params.range a double of low price, high price
 * @param {'wholesale'|'retail'} [params.priceType='wholesale'] 'wholesale' or 'retail' ONLY
 * @param {String} [params.wholesalePriceGroup='wholesale']
 * @param {String} [params.retailPriceGroup='retail']
 * @param {String} [params.currencyCode='USD']
 * @param {String} [params.locale='en_US']
 * @returns {String}
 */

export const renderPriceRangeType = ({
  currencyCode,
  locale,
  range,
  ...restParams
}: RenderPriceRangeByTypeParams): string => {
  const [low, high] = resolvedPriceRangeByType({ range, ...restParams });

  return pricesToString({
    prices: low === high ? [low] : [low, high],
    currencyCode,
    locale,
  });
};

const priceParamsWithType = (
  {
    range,
    wholesalePriceGroup,
    retailPriceGroup,
    currencyCode,
    locale,
  }: {
    range: PriceRange,
    wholesalePriceGroup: string,
    retailPriceGroup: string,
    currencyCode?: string,
    locale?: string,
  },
  priceType?: WithPricePriceType,
): PriceParamsWithType => ({
  range,
  priceType,
  wholesalePriceGroup,
  retailPriceGroup,
  currencyCode,
  locale,
});

// We should show externalPrice when the following conditions are met:
// - wholesale price is displayed
// - showExternalPriceWhenEqualToWholesalePrice is true
// else
// - wholesale price is not displayed OR wholesale price does not equal external price
const shouldShowExternalPrice = (
  params: {|
    currencyCode?: string,
    externalPriceRange?: null | [number] | [number, number],
    locale?: string,
    overridePriceType?: PriceType,
    priceType?: PriceType,
    range: PriceRange,
    retailPriceGroup: string,
    showExternalPriceWhenEqualToWholesalePrice?: boolean,
    usesExternalPrices?: boolean,
    wholesalePriceGroup: string,
    wholesaleRepresentsDiscountedAmount?: boolean,
  |},
  str: string,
): boolean => {
  // wholesale price is is not displayed
  if (!['wholesale', 'external', 'both'].includes(params.priceType))
    return false;

  // showExternalPriceWhenEqualToWholesalePrice is true
  if (params.showExternalPriceWhenEqualToWholesalePrice) return true;

  const wholesalePriceString = renderPriceRangeType(
    priceParamsWithType(params, 'wholesale'),
  );
  // wholesale price does not equal external price
  return wholesalePriceString !== str;
};

const externalPriceString = (params: {|
  range: PriceRange,
  priceType?: PriceType,
  overridePriceType?: PriceType,
  wholesalePriceGroup: string,
  retailPriceGroup: string,
  currencyCode?: string,
  locale?: string,
  usesExternalPrices?: boolean,
  showExternalPriceWhenEqualToWholesalePrice?: boolean,
  externalPriceRange?: null | [number] | [number, number],
  wholesaleRepresentsDiscountedAmount?: boolean,
  priceType?: PriceType,
|}): string | null => {
  if (!params.usesExternalPrices) return null;
  if (!params.externalPriceRange) return null;
  if (!['wholesale', 'external', 'both'].includes(params.priceType))
    return null;

  const str = pricesToString({
    // I don't understand why this was a flow error
    // $FlowFixMe[incompatible-call]
    prices: params.externalPriceRange,
    ...params,
  });

  return shouldShowExternalPrice(params, str) ? str : null;
};

export const isPriceEqualExternal = (
  price?: PriceGroupToMonetaryAmountMap,
  range?: PriceRange,
  externalPriceRange?: null | [number] | [number, number],
  priceType: PriceType,
  wholesalePriceGroup: string,
  retailPriceGroup: string,
  priceGroup: string = priceType === 'retail'
    ? retailPriceGroup
    : wholesalePriceGroup,
): ?boolean => {
  if (range === undefined) {
    return (
      price &&
      externalPriceRange &&
      price[priceGroup] === externalPriceRange[0] &&
      externalPriceRange.length === 1
    );
  }
  return (
    externalPriceRange &&
    range[0][priceGroup] === externalPriceRange[0] &&
    range[range.length - 1][priceGroup] ===
      externalPriceRange[externalPriceRange.length - 1]
  );
};

const strikethrough = (str: string): string => `<del>${str}</del>`;

/**
 * Renders a price range as an HTML-safe string. Accounts for price type setting,
 * high low, and price groups.
 *
 * @memberof modules/price
 * @param {Object} params
 * @param {Price[]} params.range a double of low price, high price
 * @param {String} [params.priceType='wholesale']
 * @param {String} [params.wholesalePriceGroup='wholesale']
 * @param {String} [params.retailPriceGroup='retail']
 * @param {String} [params.currencyCode='USD']
 * @param {String} [params.locale='en_US']
 * @param {Boolean} [params.usesExternalPrice=false]
 * @param {?[number, number]} [params.externalPriceRange]
 * @returns {String}
 */

// strictly for type refinement
const strikeThroughProps = ({
  priceType,
  range,
  retailPriceGroup,
  wholesalePriceGroup,
  wholesaleRepresentsDiscountedAmount,
}: {|
  currencyCode?: string,
  externalPriceRange?: null | [number] | [number, number],
  locale?: string,
  overridePriceType?: PriceType,
  priceType?: PriceType,
  range: PriceRange,
  retailPriceGroup: string,
  showExternalPriceWhenEqualToWholesalePrice?: boolean,
  usesExternalPrices?: boolean,
  wholesalePriceGroup: string,
  wholesaleRepresentsDiscountedAmount?: boolean,
|}): StrikeThroughRetailParams => ({
  priceType,
  range,
  retailPriceGroup,
  wholesalePriceGroup,
  wholesaleRepresentsDiscountedAmount,
});

export const renderPriceRange = (params: {|
  range: PriceRange,
  priceType?: PriceType,
  overridePriceType?: PriceType,
  wholesalePriceGroup: string,
  retailPriceGroup: string,
  currencyCode?: string,
  locale?: string,
  usesExternalPrices?: boolean,
  showExternalPriceWhenEqualToWholesalePrice?: boolean,
  externalPriceRange?: null | [number] | [number, number],
  wholesaleRepresentsDiscountedAmount?: boolean,
|}): string => {
  const resolvedPriceType = params.overridePriceType || params.priceType;
  const resolvedTypeParams = { ...params, priceType: resolvedPriceType };

  if (resolvedPriceType === 'none') return '';
  const exPriceStr = externalPriceString(resolvedTypeParams);
  const exPriceArr = typeof exPriceStr === 'string' ? [exPriceStr] : [];

  if (resolvedPriceType === 'external') return exPriceArr.join('');

  const wholesalePriceArr =
    (['wholesale', 'both'].includes(resolvedPriceType) &&
      (exPriceArr.length ? strikethrough : identity)(
        renderPriceRangeType(
          priceParamsWithType(resolvedTypeParams, 'wholesale'),
        ),
      )) ||
    [];

  const retailPriceArr =
    (['retail', 'both'].includes(resolvedPriceType) &&
      (strikeThroughRetail(strikeThroughProps(params))
        ? strikethrough
        : identity)(
        renderPriceRangeType(priceParamsWithType(resolvedTypeParams, 'retail')),
      )) ||
    [];

  return flatten([wholesalePriceArr, exPriceArr, retailPriceArr]).join(' / ');
};

/**
 * Renders a price as an HTML-safe string. Accounts for price type setting,
 * high low, and price groups.
 *
 * @memberof modules/price
 * @param {Object} params
 * @param {Price} params.price
 * @param {String} [params.priceType='wholesale']
 * @param {String} [params.wholesalePriceGroup='wholesale']
 * @param {String} [params.retailPriceGroup='retail']
 * @param {String} [params.currencyCode='USD']
 * @param {String} [params.locale='en_US']
 * @returns {String}
 */
export const renderPrice = ({
  price,
  ...params
}: {|
  price: PriceGroupToMonetaryAmountMap,
  priceType: PriceType,
  wholesalePriceGroup: string,
  retailPriceGroup: string,
  currencyCode?: string,
  locale?: string,
  wholesaleRepresentsDiscountedAmount?: boolean,
|}): string =>
  renderPriceRange({
    ...params,
    range: [price, price],
  });

/**
 * Creates a double of prices containing all the lowest prices in the list in
 * the first element and all the highest in the second element.
 *
 * @memberof modules/price
 * @param {Object} params
 * @param {Price[]} params.prices
 * @returns {Price[]}
 */
export const rangeForPriceList = ({
  prices,
}: {
  prices: PriceGroupToMonetaryAmountMap[],
}): PriceRange => [
  // low price
  prices.reduce(
    (acc, price) =>
      mergePrices({
        left: acc,
        right: price,
        actor: (a, b) => Math.min(a, b),
      }),
    prices[0] || { ...null },
  ),
  // high price
  prices.reduce(
    (acc, price) =>
      mergePrices({
        left: acc,
        right: price,
        actor: (a, b) => Math.max(a, b),
      }),
    prices[0] || { ...null },
  ),
];

export const zeroPrice = (): PriceGroupToMonetaryAmountMap => ({
  wholesale: 0,
  retail: 0,
});

export const zeroResolvedPrice = (): ResolvedPrice => ({
  wholesale: 0,
  retail: 0,
});

export const normalizePriceToRange = (
  price?: PriceGroupToMonetaryAmountMap,
  range?: PriceRange,
): PriceRange => {
  if (range) {
    return range;
  }
  if (price) {
    return [price, price];
  }

  return [zeroPrice(), zeroPrice()];
};

export const resolvedPriceNumberForComparison = (
  price: ResolvedPrice = zeroResolvedPrice(),
): number =>
  // this is an ugly hack, but come on, flow, this can only be a
  // Maybe<number>
  price.wholesale || ((Object.values(price)[0]: any): number) || 0;

export const resolvedPriceForComparison = (
  props: {
    price: PriceGroupToMonetaryAmountMap,
    wholesalePriceGroup: string,
    retailPriceGroup: string,
  },
  // the spread operator here gets around a flow error. width subtyping only
  // goes in one direction, meaning that i would have to specify all the
  // properties of resolvedPrices' prop object on the above props object if i
  // want to pass it in directly. basically flow won't accept a narrower object
  // type in this case, but it will accept a "subtype" of resolvedPrices' props
  // object type (basically a partial implementation of the object type)
  // which the above props does not count as being apparently
): number => resolvedPriceNumberForComparison(resolvedPrices({ ...props }));

export const resolvedPriceForComparisonWithDefaulting = (props: {
  price: PriceGroupToMonetaryAmountMap,
}): number =>
  resolvedPriceForComparison({
    ...props,
    wholesalePriceGroup: 'wholesale',
    retailPriceGroup: 'retail',
  });

export const priceIsGreaterThan = ({
  toBeGreater,
  floor,
  wholesalePriceGroup,
  retailPriceGroup,
}: {
  toBeGreater: PriceGroupToMonetaryAmountMap,
  floor: PriceGroupToMonetaryAmountMap,
  wholesalePriceGroup: string,
  retailPriceGroup: string,
}): boolean =>
  resolvedPriceNumberForComparison(
    resolvedPrices({
      price: toBeGreater,
      wholesalePriceGroup,
      retailPriceGroup,
    }),
  ) >
  resolvedPriceNumberForComparison(
    resolvedPrices({
      price: floor,
      wholesalePriceGroup,
      retailPriceGroup,
    }),
  );
